import {
  MEDIA_DEFAULT_PRODUCT_STATUSES,
  PRODUCT_STATUSES,
} from '@shared/types/product-status';
import {
  MEDIA_CONTENT_TYPES,
  MEDIA_DIMENSION_PX_MAX,
  MEDIA_FILE_SIZE_KB_MAX,
  MEDIA_FILE_TYPES,
} from '@shared/types/product-media';
import { splitBrandRefs } from '@shared/brand-filter';
import { ALT_TEXT_STATUSES } from '@shared/product-image-alt-text';
import type { MediaQueryParams } from '@/lib/api';
import { TABLE_PAGING_URL } from '@/lib/table-paging';
import {
  urlBool,
  urlColsMask,
  urlIdList,
  urlInt,
  urlList,
  urlSort,
  urlText,
} from '@/lib/url-state/codecs';
import type { UrlValues } from '@/lib/url-state/url-schema';

export { MEDIA_DIMENSION_PX_MAX, MEDIA_FILE_SIZE_KB_MAX };

/** Columns the list sorts by (their DataTable ids). */
export const MEDIA_SORT_COLUMNS = [
  'media_content_type',
  'product_title',
  'vendor_name',
  'dimensions',
  'file_size',
  'duration_ms',
  'format',
  'product_position',
  'variant_assignment_count',
  'shopify_updated_at',
] as const;

const PRODUCT_STATUSES_FIELD = urlList('productStatuses', {
  values: PRODUCT_STATUSES,
  default: MEDIA_DEFAULT_PRODUCT_STATUSES,
});

/** Keys only the page before T-196 wrote (it always wrote `sortField` and `sortDirection`). */
const OLD_ONLY_KEYS = ['query', 'vendors', 'sortField', 'sortDirection'];

/**
 * Shopify statuses, active and draft by default. Before T-196 the page left `productStatuses` out
 * when every status was shown: an old link or saved view without it means every status.
 */
const urlMediaProductStatuses: typeof PRODUCT_STATUSES_FIELD = {
  ...PRODUCT_STATUSES_FIELD,
  read(params) {
    if (!params.has('productStatuses') && OLD_ONLY_KEYS.some((key) => params.has(key))) return [];
    return PRODUCT_STATUSES_FIELD.read(params);
  },
};

/**
 * /media in the URL. Until T-196 the keys were `query`, `vendors` (brand names) and
 * `sortField`/`sortDirection`; they're still read, so bookmarks and old links open the same list
 * (decision 30 c). `vendorIds` holds brand ids; a name from an old link stays a name until the
 * brands have loaded (`resolveBrandRefs`). The other filters kept their names. `cols` comes only
 * from a link or a saved view: column choices are saved in storage.
 */
export const MEDIA_URL = {
  q: urlText('q', { aliases: ['query'] }),
  vendorIds: urlIdList('vendorIds', { aliases: ['vendors'] }),
  contentTypes: urlList('contentTypes', { values: MEDIA_CONTENT_TYPES }),
  fileTypes: urlList<string>('fileTypes', { values: MEDIA_FILE_TYPES.map((t) => t.value) }),
  productStatuses: urlMediaProductStatuses,
  minFileSizeKb: urlInt('minFileSizeKb', { min: 0, max: MEDIA_FILE_SIZE_KB_MAX, default: 0 }),
  maxFileSizeKb: urlInt('maxFileSizeKb', {
    min: 0,
    max: MEDIA_FILE_SIZE_KB_MAX,
    default: MEDIA_FILE_SIZE_KB_MAX,
  }),
  minShortestSide: urlInt('minShortestSide', { min: 0, max: MEDIA_DIMENSION_PX_MAX, default: 0 }),
  maxShortestSide: urlInt('maxShortestSide', {
    min: 0,
    max: MEDIA_DIMENSION_PX_MAX,
    default: MEDIA_DIMENSION_PX_MAX,
  }),
  nonSquareOnly: urlBool('nonSquareOnly'),
  /** Alt text statuses (T-165). */
  altStatuses: urlList('altStatuses', { values: ALT_TEXT_STATUSES }),
  sort: urlSort({
    columns: MEDIA_SORT_COLUMNS,
    default: { id: 'product_position', dir: 'asc' },
    legacy: { field: 'sortField', direction: 'sortDirection' },
  }),
  cols: urlColsMask(),
  ...TABLE_PAGING_URL,
};

export type MediaView = UrlValues<typeof MEDIA_URL>;

/** Remembered per team: the sidebar brings back the last filters and sort. */
export const MEDIA_VIEW_ID = 'media';

type Range = { min: number; max: number };

/** The file size and dimension ranges, each with its bounds in order. */
export function mediaRanges(view: MediaView): { fileSize: Range; dimensions: Range } {
  return {
    fileSize: { min: view.minFileSizeKb, max: Math.max(view.minFileSizeKb, view.maxFileSizeKb) },
    dimensions: {
      min: view.minShortestSide,
      max: Math.max(view.minShortestSide, view.maxShortestSide),
    },
  };
}

/** The list request for a view (every page; the caller adds `page` and `limit`). */
export function mediaListQuery(
  view: MediaView,
  ranges: { fileSize: Range; dimensions: Range } = mediaRanges(view)
): MediaQueryParams {
  const brands = splitBrandRefs(view.vendorIds);
  const list = <T>(values: T[]) => (values.length > 0 ? values : undefined);
  return {
    query: view.q || undefined,
    vendorIds: list(brands.ids),
    vendors: list(brands.names),
    contentTypes: list(view.contentTypes),
    fileTypes: list(view.fileTypes),
    productStatuses: list(view.productStatuses),
    minFileSizeKb: ranges.fileSize.min,
    maxFileSizeKb: ranges.fileSize.max,
    minShortestSide: ranges.dimensions.min,
    maxShortestSide: ranges.dimensions.max,
    nonSquareOnly: view.nonSquareOnly || undefined,
    altStatuses: list(view.altStatuses),
    sortField: view.sort.id,
    sortDirection: view.sort.dir === 'desc' ? 'descending' : 'ascending',
  };
}
